import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  readSprintPlanDefaultBoardId,
  shouldSyncSprintPlanStorageKey,
  SPRINT_PLAN_BOARD_CHANGED_EVENT,
} from '../constants/sprintPlanBoard';

/**
 * Default Jira board from Sprint Plan ("Save as default board"), scoped by current product.
 * Stays in sync across tabs and when Sprint Plan updates the same product.
 */
export function useDefaultBoardId(): string | null {
  const { currentProduct } = useAuth();
  const productId = currentProduct?.id ?? null;

  const read = useCallback((): string | null => {
    return readSprintPlanDefaultBoardId(productId);
  }, [productId]);

  const [boardId, setBoardId] = useState<string | null>(() => read());

  useEffect(() => {
    setBoardId(read());
  }, [read, productId]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (shouldSyncSprintPlanStorageKey(e.key, productId)) setBoardId(read());
    };
    const onBoardEvent = (ev: Event) => {
      const d = (ev as CustomEvent<{ productId?: string }>).detail;
      if (productId && d?.productId != null && d.productId !== productId) return;
      setBoardId(read());
    };
    const onFocus = () => setBoardId(read());
    window.addEventListener('storage', onStorage);
    window.addEventListener(SPRINT_PLAN_BOARD_CHANGED_EVENT, onBoardEvent);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SPRINT_PLAN_BOARD_CHANGED_EVENT, onBoardEvent);
      window.removeEventListener('focus', onFocus);
    };
  }, [read, productId]);

  return boardId;
}
